export type TDataPoint = [number,number]
export default function smoothSpline(points: TDataPoint[],smoothing: number = 0.9): (x: number) => number {
  if (points.length === 0) throw new Error("Points array cannot be empty.");

  // 1. Sort points by x-coordinate
  const sorted = [...points].sort((a, b) => a[0] - b[0]);
  const n = sorted.length;

  if (n === 1) return () => sorted[0][1];

  const x = sorted.map((p) => p[0]);
  const y = sorted.map((p) => p[1]);

  if (n === 2) {
    return (targetX: number) => {
      const slope = (y[1] - y[0]) / (x[1] - x[0]);
      return y[0] + slope * (targetX - x[0]);
    };
  }

  // 2. Compute step sizes
  const h = new Array<number>(n - 1);
  for (let i = 0; i < n - 1; i++) {
    h[i] = x[i + 1] - x[i];
    if (h[i] <= 0) throw new Error("Duplicate x-coordinates provided.");
  }

  // Clamp smoothing parameter alpha in [0, 1)
  const alpha = Math.min(Math.max(smoothing, 0), 0.9999);
  // Weight factor for smoothing penalty
  const lambda = alpha === 0 ? 0 : (1 - alpha) / alpha;

  // 3. Build tridiagonal systems for smoothing matrix equations
  const Q = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  const R = Array.from({ length: n - 2 }, () => new Array<number>(n - 2).fill(0));

  for (let i = 0; i < n - 2; i++) {
    Q[i][i] = 1 / h[i];
    Q[i + 1][i] = -1 / h[i] - 1 / h[i + 1];
    Q[i + 2][i] = 1 / h[i + 1];
  }

  for (let i = 0; i < n - 2; i++) {
    R[i][i] = (h[i] + h[i + 1]) / 3;
    if (i < n - 3) {
      R[i][i + 1] = h[i + 1] / 6;
      R[i + 1][i] = h[i + 1] / 6;
    }
  }

  // Calculate smoothed y-values (fitted target vector f)
  // If lambda === 0, f = y (exact fit)
  const fittedY = [...y];

  if (lambda > 0) {
    // Perform simplified relaxation solver to smooth fitted points
    for (let iter = 0; iter < 100; iter++) {
      for (let i = 1; i < n - 1; i++) {
        const prev = fittedY[i - 1];
        const next = fittedY[i + 1];
        const avg = (prev + next) / 2;
        fittedY[i] = fittedY[i] + (1 - alpha) * (avg - fittedY[i]);
      }
    }
  }

  // 4. Fit Natural Cubic Spline over the smoothed points (fittedY)
  const a = [...fittedY];
  const b = new Array<number>(n - 1).fill(0);
  const c = new Array<number>(n).fill(0);
  const d = new Array<number>(n - 1).fill(0);

  const alphaVec = new Array<number>(n - 1).fill(0);
  for (let i = 1; i < n - 1; i++) {
    alphaVec[i] =
      (3 / h[i]) * (a[i + 1] - a[i]) - (3 / h[i - 1]) * (a[i] - a[i - 1]);
  }

  const l = new Array<number>(n).fill(0);
  const mu = new Array<number>(n).fill(0);
  const z = new Array<number>(n).fill(0);

  l[0] = 1;
  for (let i = 1; i < n - 1; i++) {
    l[i] = 2 * (x[i + 1] - x[i - 1]) - h[i - 1] * mu[i - 1];
    mu[i] = h[i] / l[i];
    z[i] = (alphaVec[i] - h[i - 1] * z[i - 1]) / l[i];
  }
  l[n - 1] = 1;

  for (let j = n - 2; j >= 0; j--) {
    c[j] = z[j] - mu[j] * c[j + 1];
    b[j] = (a[j + 1] - a[j]) / h[j] - (h[j] * (c[j + 1] + 2 * c[j])) / 3;
    d[j] = (c[j + 1] - c[j]) / (3 * h[j]);
  }

  // 5. Evaluation closure
  return (targetX: number) => {
    if (targetX <= x[0]) return a[0];
    if (targetX >= x[n - 1]) return a[n - 1];

    let low = 0;
    let high = n - 2;
    let i = 0;

    while (low <= high) {
      const mid = (low + high) >> 1;
      if (targetX >= x[mid] && targetX <= x[mid + 1]) {
        i = mid;
        break;
      }
      if (targetX < x[mid]) high = mid - 1;
      else low = mid + 1;
    }

    const dx = targetX - x[i];
    return a[i] + b[i] * dx + c[i] * dx * dx + d[i] * dx * dx * dx;
  };
}